import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useMyPermissions from "../hooks/useMyPermissions";
import { getCurrentSalonId } from "../utils/currentSalon";
import { normalizeForSearch } from "../utils/normalizeSearch";
import SortableTh from "../components/common/SortableTh";
import ServiceSearch from "../components/services/ServiceSearch";
import ServiceImportWizard from "../components/services/ServiceImportWizard";
import ImportProductsModal from "../components/modals/ImportProductsModal";
import SBINModal from "../components/modals/SBINModal";
import ModalCloseX from "../components/common/ModalCloseX";
import { listCategories } from "../api/services";
import { API_BASE as API } from "../api/base";
import useTableSort from "../hooks/useTableSort";



const MANAGER_ROLES = [
  "admin",
  "host",
  "owner",
  "manager",
  "independent_professional",
];

const DURATION_OPTIONS = Array.from({ length: 32 }, (_, i) => (i + 1) * 15);
const AI_PRICING_RESPONSE_OPTIONS = [
  { value: "", label: "Use salon default" },
  { value: "EXACT_PRICE", label: "Exact price" },
  { value: "STARTING_AT_PRICE", label: "Starting at price" },
  { value: "HIDDEN_PRICE", label: "Hide price / consultation required" },
];



function snapDurationToInterval(value) {

  if (!Number.isFinite(value) || value <= 0) return 15;

  const snapped = Math.round(value / 15) * 15;

  return Math.min(480, Math.max(15, snapped));

}



function formatPrice(value) {

  const num = Number(value);

  if (Number.isNaN(num)) return "$0.00";

  return `$${num.toFixed(2)}`;

}



function formatDurationOption(minutes) {
  if (minutes <= 45) return `${minutes} min`;
  if (minutes % 60 === 0) return `${minutes / 60} hr`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${hours} hr ${remainder} min`;
}

function isCategoryCompatible(category, productMode) {
  if (!category) return false;
  if (productMode) return ["product", "both"].includes(category.kind);
  return ["service", "both"].includes(category.kind);
}

export default function ManageServices() {
  const navigate = useNavigate();
  const { token, role: ctxRole, user } = useAuth();
  const modalText = "text-base md:text-lg";
  const labelText =

    "text-sm md:text-base font-semibold tracking-wide text-slate-500";

  const fieldClass =

    "h-11 md:h-12 px-4 text-base md:text-lg rounded-xl border border-slate-300 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200";

  const sectionCard =

    "rounded-2xl border border-slate-200 bg-white shadow-sm p-6";

  const btnPrimary =
    "h-11 md:h-12 px-6 rounded-full bg-slate-900 text-white text-base md:text-lg font-semibold hover:bg-slate-800 disabled:opacity-60";
  const btnSecondary =
    "h-11 md:h-12 px-6 rounded-full border border-slate-300 text-slate-700 text-base md:text-lg font-semibold hover:bg-slate-50";
  const tableHeaderClass =
    "px-4 py-3 text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase";
  const tableHeaderButtonClass = "text-slate-500 hover:text-slate-900";


  const salonId = getCurrentSalonId(user);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [serviceQuery, setServiceQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isProductImportOpen, setIsProductImportOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [categoryFilterKind, setCategoryFilterKind] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedType, setSelectedType] = useState(null);
  const [bulkCategoryId, setBulkCategoryId] = useState(null);
  const [bulkError, setBulkError] = useState("");
  const [bulkNotice, setBulkNotice] = useState("");
  const [bulkSuccess, setBulkSuccess] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkCreatePending, setBulkCreatePending] = useState(false);
  const [activeSavingIds, setActiveSavingIds] = useState(new Set());
  const [activeToggleError, setActiveToggleError] = useState("");
  const [onlineSavingIds, setOnlineSavingIds] = useState(new Set());
  const [onlineToggleError, setOnlineToggleError] = useState("");
  const [showPriceSavingIds, setShowPriceSavingIds] = useState(new Set());
  const [showPriceToggleError, setShowPriceToggleError] = useState("");
  const [bulkFlagSaving, setBulkFlagSaving] = useState("");
  const [showAdvancedAiPricing, setShowAdvancedAiPricing] = useState(false);
  const [form, setForm] = useState({
    name: "",
    duration_minutes: 60,
    base_price: 0,
    ai_pricing_response_mode: "",
    ai_minimum_price: "",
    ai_pricing_disclaimer: "",
    ai_hidden_price_message: "",
    description: "",
    is_active: true,
    show_price_online: true,
    is_product: false,
    sku: "",
    category_id: null,
  });
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    kind: "both",
    sort_order: 0,
    is_active: true,
  });
  const categoryFormRef = useRef(null);
  const selectAllRef = useRef(null);
  const activeAllRef = useRef(null);
  const onlineAllRef = useRef(null);
  const showPriceAllRef = useRef(null);
  const [assignToAllStaff, setAssignToAllStaff] = useState(true);
  const [skuError, setSkuError] = useState("");
  const isProduct = !!form.is_product;
  const rawDuration = Number(form.duration_minutes);
  const snappedDuration = useMemo(
    () => snapDurationToInterval(rawDuration),
    [rawDuration]
  );
  const showDurationHint =
    !isProduct &&
    Number.isFinite(rawDuration) && rawDuration > 0 && rawDuration % 15 !== 0;
  const typeForService = (service) => (service?.is_product ? "product" : "service");

  const categoryMap = useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories]
  );

  useEffect(() => {
    if (!services.length) {
      if (selectedIds.size) {
        setSelectedIds(new Set());
        setSelectedType(null);
      }
      return;
    }
    const currentIds = new Set(services.map((service) => service.id));
    const next = new Set();
    selectedIds.forEach((id) => {
      if (currentIds.has(id)) next.add(id);
    });
    if (next.size !== selectedIds.size) {
      setSelectedIds(next);
    }
    if (next.size === 0 && selectedType) {
      setSelectedType(null);
    }
  }, [services, selectedIds, selectedType]);

  useEffect(() => {
    if (!form.category_id) return;
    const selected = categoryMap.get(form.category_id);
    if (selected && !isCategoryCompatible(selected, isProduct)) {
      setForm((prev) => ({ ...prev, category_id: null }));
    }
  }, [form.category_id, categoryMap, isProduct]);

  useEffect(() => {
    setBulkCategoryId(null);
    setBulkSuccess("");
    setBulkError("");
    setBulkNotice("");
    setBulkCreatePending(false);
  }, [selectedType]);

  const displayServices = useMemo(() => {
    const query = normalizeForSearch(serviceQuery).trim();
    if (!query) return services;
    return services.filter((service) => {
      const nameMatch = normalizeForSearch(service?.name).includes(query);
      const skuMatch = normalizeForSearch(service?.sku).includes(query);
      return nameMatch || skuMatch;
    });
  }, [services, serviceQuery]);

  const serviceSortColumns = useMemo(
    () => ({
      name: {
        type: "text",
        getValue: (row) => row?.name || "",
      },
      duration_minutes: {
        type: "number",
        getValue: (row) =>
          row?.is_product ? 0 : Number(row?.duration_minutes) || 0,
      },
      base_price: {
        type: "number",
        getValue: (row) => Number(row?.base_price) || 0,
      },
      is_active: {
        type: "number",
        getValue: (row) => (row?.is_active ? 1 : 0),
      },
      show_in_online_booking: {
        type: "number",
        getValue: (row) => (row?.show_in_online_booking ? 1 : 0),
      },
      show_price_online: {
        type: "number",
        getValue: (row) => (row?.show_price_online !== false ? 1 : 0),
      },
    }),
    []
  );

  const servicesSort = useTableSort({
    rows: displayServices,
    columns: serviceSortColumns,
    defaultSortBy: "name",
    defaultSortDir: "asc",
    fallbackKey: "name",
  });

  const sortedDisplayServices = servicesSort.sortedRows;

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      if (!!a.is_active !== !!b.is_active) {
        return a.is_active ? -1 : 1;
      }
      const orderDiff = (a.sort_order || 0) - (b.sort_order || 0);
      if (orderDiff !== 0) return orderDiff;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [categories]);

  const filteredCategories = useMemo(() => {
    if (categoryFilterKind === "service") {
      return sortedCategories.filter((category) =>
        ["service", "both"].includes(category.kind)
      );
    }
    if (categoryFilterKind === "product") {
      return sortedCategories.filter((category) =>
        ["product", "both"].includes(category.kind)
      );
    }
    if (categoryFilterKind === "both") {
      return sortedCategories.filter((category) => category.kind === "both");
    }
    return sortedCategories;
  }, [sortedCategories, categoryFilterKind]);

  const compatibleCategories = useMemo(() => {
    return sortedCategories.filter((category) =>
      isCategoryCompatible(category, isProduct)
    );
  }, [sortedCategories, isProduct]);

  const categoryOptions = useMemo(() => {
    const options = [...compatibleCategories];
    if (form.category_id) {
      const current = categoryMap.get(form.category_id);
      if (current && !options.find((cat) => cat.id === current.id)) {
        options.unshift({ ...current, incompatible: true });
      }
    }
    return options;
  }, [compatibleCategories, form.category_id, categoryMap]);

  const groupedServices = useMemo(() => {
    if (!groupByCategory) return null;
    const byCategory = new Map();
    const uncategorized = [];
    sortedDisplayServices.forEach((service) => {
      const category = service.category_id
        ? categoryMap.get(service.category_id)
        : null;
      if (!category) {
        uncategorized.push(service);
        return;
      }
      if (!byCategory.has(category.id)) {
        byCategory.set(category.id, []);
      }
      byCategory.get(category.id).push(service);
    });

    const groups = [];
    sortedCategories.forEach((category) => {
      const items = byCategory.get(category.id) || [];
      if (items.length) {
        groups.push({ category, items });
      }
    });
    groups.push({ category: null, items: uncategorized });
    return groups;
  }, [categoryMap, groupByCategory, sortedCategories, sortedDisplayServices]);

  const visibleTypes = useMemo(() => {
    return new Set(displayServices.map((service) => typeForService(service)));
  }, [displayServices]);

  const selectableType = useMemo(() => {
    if (selectedType) return selectedType;
    if (visibleTypes.size === 1) {
      return Array.from(visibleTypes)[0];
    }
    return null;
  }, [selectedType, visibleTypes]);

  const visibleSelectableItems = useMemo(() => {
    if (!selectableType) return [];
    return displayServices.filter(
      (service) => typeForService(service) === selectableType
    );
  }, [displayServices, selectableType]);

  const allVisibleSelected =
    visibleSelectableItems.length > 0 &&
    visibleSelectableItems.every((service) => selectedIds.has(service.id));
  const someVisibleSelected =
    visibleSelectableItems.length > 0 &&
    visibleSelectableItems.some((service) => selectedIds.has(service.id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        !allVisibleSelected && someVisibleSelected;
    }
  }, [allVisibleSelected, someVisibleSelected]);

  const getVisibleFlagState = (field, defaultValue = false) => {
    if (!displayServices.length) {
      return { all: false, some: false, nextValue: true };
    }
    const values = displayServices.map((service) =>
      service[field] === undefined || service[field] === null
        ? defaultValue
        : Boolean(service[field])
    );
    const all = values.every(Boolean);
    const some = values.some(Boolean);
    return { all, some, nextValue: !all };
  };

  const activeVisibleState = getVisibleFlagState("is_active", false);
  const onlineVisibleState = getVisibleFlagState("show_in_online_booking", false);
  const showPriceVisibleState = getVisibleFlagState("show_price_online", true);

  useEffect(() => {
    if (activeAllRef.current) {
      activeAllRef.current.indeterminate =
        !activeVisibleState.all && activeVisibleState.some;
    }
    if (onlineAllRef.current) {
      onlineAllRef.current.indeterminate =
        !onlineVisibleState.all && onlineVisibleState.some;
    }
    if (showPriceAllRef.current) {
      showPriceAllRef.current.indeterminate =
        !showPriceVisibleState.all && showPriceVisibleState.some;
    }
  }, [activeVisibleState, onlineVisibleState, showPriceVisibleState]);

  const bulkCategoryOptions = useMemo(() => {
    if (!selectedType) return [];
    const allowedKinds =
      selectedType === "product" ? ["product", "both"] : ["service", "both"];
    return sortedCategories.filter((category) =>
      allowedKinds.includes(category.kind)
    );
  }, [selectedType, sortedCategories]);


  const role = useMemo(
    () => (ctxRole || localStorage.getItem("role") || "").toLowerCase(),
    [ctxRole]
  );
  const canManageOnlineBooking =
    role === "owner" ||
    role === "manager" ||
    role === "admin" ||
    role === "host" ||
    role === "independent_professional";
  const storedToken = token;
  const { permissions: effectivePermissions, loading: permissionsLoading } =
    useMyPermissions({ enabled: Boolean(storedToken && salonId) });
  const canAccessServices =
    ["admin", "host"].includes(role) ||
    Boolean(user?.is_super_admin) ||
    (effectivePermissions === null
      ? MANAGER_ROLES.includes(role)
      : Boolean(effectivePermissions?.["services.manage"]));

  useEffect(() => {
    if (!storedToken) {
      navigate("/login?next=/manager/manage/services", { replace: true });
      return;
    }
    if (!salonId) {
      setError("Select a salon to manage services.");
      const timeout = setTimeout(() => {
        navigate("/my-salons", { replace: true });
      }, 600);
      return () => clearTimeout(timeout);
    }
    if (permissionsLoading || effectivePermissions === null) {
      return;
    }
    if (!canAccessServices) {
      navigate("/my-bookings", { replace: true });
    }
  }, [
    storedToken,
    salonId,
    navigate,
    permissionsLoading,
    effectivePermissions,
    canAccessServices,
  ]);


  const headers = useMemo(
    () =>
      storedToken
        ? {
          Authorization: `Bearer ${storedToken}`,
          "Content-Type": "application/json",
        }
        : { "Content-Type": "application/json" },
    [storedToken]
  );


  useEffect(() => {

    if (!storedToken || !salonId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setCategoriesLoading(true);
      setError("");
      setCategoryError("");
      try {
        const servicesRes = await fetch(
          `${API}/services?salon_id=${salonId}&include_products=true`,
          { headers }
        );
        if (!servicesRes.ok) {
          const detail = await servicesRes.text();
          throw new Error(detail || "Failed to load services");
        }
        setServices(await servicesRes.json());
        const categoriesData = await listCategories({}, storedToken);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } catch (err) {
        setError(err.message || "Failed to load services");
        setCategoryError(err.message || "Failed to load categories");
      } finally {
        setLoading(false);
        setCategoriesLoading(false);
      }
    };


    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedToken, headers, salonId]);

  const refreshServices = async () => {
    if (!salonId) return;
    try {
      const res = await fetch(
        `${API}/services?salon_id=${salonId}&include_products=true`,
        { headers }
      );
      if (res.ok) {
        setServices(await res.json());
      }
    } catch (err) {
      setError(err.message || "Failed to refresh services");
    }
  };

  const refreshCategories = async () => {
    if (!salonId) return;
    setCategoriesLoading(true);
    setCategoryError("");
    try {
      const categoriesData = await listCategories({}, storedToken);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (err) {
      setCategoryError(err.message || "Failed to refresh categories");
    } finally {
      setCategoriesLoading(false);
    }
  };


  const resetForm = () => {
    setEditingId(null);
    setForm({
      name: "",
      duration_minutes: 60,
      base_price: 0,
      ai_pricing_response_mode: "",
      ai_minimum_price: "",
      ai_pricing_disclaimer: "",
      ai_hidden_price_message: "",
      description: "",
      is_active: true,
      show_price_online: true,
      is_product: false,
      sku: "",
      category_id: null,
    });
    setSkuError("");
    setAssignToAllStaff(true);
    setShowAdvancedAiPricing(false);
  };

  const handleTypeChange = (checked) => {
    setForm((prev) => {
      const nextDuration = checked ? 0 : prev.duration_minutes || 60;
      const nextSku = checked ? prev.sku : "";
      return {
        ...prev,
        is_product: checked,
        duration_minutes: nextDuration,
        sku: nextSku,
      };
    });
    setSkuError("");
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    const nameValue = form.name.trim();
    const trimmedSku = String(form.sku || "").trim();
    if (!nameValue || !salonId) {
      setError("Name is required.");
      return;
    }
    if (isProduct && !trimmedSku) {
      setSkuError("SKU is required for products.");
      setError("");
      return;
    }
    setSaving(true);
    setError("");
    setSkuError("");
    try {
      const payload = {
        salon_id: salonId ? Number(salonId) : undefined,
        name: nameValue,
        description: form.description || null,
        duration_minutes: isProduct ? 0 : snappedDuration || 0,
        base_price: Number(form.base_price) || 0,
        ai_pricing_response_mode: form.ai_pricing_response_mode || null,
        ai_minimum_price:
          form.ai_minimum_price === "" || form.ai_minimum_price === null
            ? null
            : Number(form.ai_minimum_price),
        ai_pricing_disclaimer: form.ai_pricing_disclaimer || null,
        ai_hidden_price_message: form.ai_hidden_price_message || null,
        is_active: !!form.is_active,
        show_price_online: form.show_price_online !== false,
        is_product: isProduct,
        sku: isProduct ? trimmedSku : null,
        category_id:
          form.category_id === null || form.category_id === undefined
            ? null
            : Number(form.category_id),
      };
      if (!editingId) {
        payload.assign_to_all_staff = !!assignToAllStaff;
      }

      const url = editingId
        ? `${API}/services/${editingId}`
        : `${API}/services`;
      const method = editingId ? "PUT" : "POST";



      const res = await fetch(url, {

        method,

        headers,

        body: JSON.stringify(payload),

      });



      if (!res.ok) {

        const msg = await res.text();

        throw new Error(msg || "Failed to save service");

      }



      resetForm();

      await refreshServices();

    } catch (err) {

      setError(err.message || "Failed to save service");

    } finally {

      setSaving(false);

    }

  };



  const handleEdit = (service) => {
    setEditingId(service.id);
    setForm({
      name: service.name || "",
      duration_minutes: service.is_product ? 0 : service.duration_minutes || 60,
      base_price: service.base_price ?? 0,
      ai_pricing_response_mode: service.ai_pricing_response_mode || "",
      ai_minimum_price:
        service.ai_minimum_price === null || service.ai_minimum_price === undefined
          ? ""
          : service.ai_minimum_price,
      ai_pricing_disclaimer: service.ai_pricing_disclaimer || "",
      ai_hidden_price_message: service.ai_hidden_price_message || "",
      description: service.description || "",
      is_active: service.is_active ?? true,
      show_price_online: service.show_price_online !== false,
      is_product: !!service.is_product,
      sku: service.sku || "",
      category_id: service.category_id ?? null,
    });
    setSkuError("");
    setShowAdvancedAiPricing(
      Boolean(
        service.ai_pricing_response_mode ||
          service.ai_minimum_price ||
          service.ai_pricing_disclaimer ||
          service.ai_hidden_price_message
      )
    );
  };


  const handleDelete = async (serviceId) => {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/services/${serviceId}`, {
        method: "DELETE",
        headers,

      });

      if (!res.ok) {

        const msg = await res.text();

        throw new Error(msg || "Failed to delete service");

      }

      await refreshServices();

      if (editingId === serviceId) {

        resetForm();

      }

    } catch (err) {

      setError(err.message || "Failed to delete service");

    } finally {
      setSaving(false);
    }
  };

  const handleActiveToggle = async (service, nextValue) => {
    if (!service) return;
    if (activeSavingIds.has(service.id)) return;
    const previousValue = !!service.is_active;
    if (previousValue === nextValue) return;
    setActiveToggleError("");
    setServices((prev) =>
      prev.map((item) =>
        item.id === service.id ? { ...item, is_active: nextValue } : item
      )
    );
    setActiveSavingIds((prev) => {
      const next = new Set(prev);
      next.add(service.id);
      return next;
    });
    try {
      const res = await fetch(`${API}/services/${service.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ is_active: nextValue }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to update active status");
      }
      const updated = await res.json();
      setServices((prev) =>
        prev.map((item) =>
          item.id === service.id ? { ...item, ...updated } : item
        )
      );
    } catch (err) {
      setServices((prev) =>
        prev.map((item) =>
          item.id === service.id ? { ...item, is_active: previousValue } : item
        )
      );
      setActiveToggleError(
        err.message || "Failed to update active status. Please try again."
      );
    } finally {
      setActiveSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(service.id);
        return next;
      });
    }
  };

  const handleOnlineToggle = async (service, nextValue) => {
    if (!service) return;
    if (!canManageOnlineBooking) return;
    if (onlineSavingIds.has(service.id)) return;
    const previousValue = !!service.show_in_online_booking;
    if (previousValue === nextValue) return;
    setOnlineToggleError("");
    setServices((prev) =>
      prev.map((item) =>
        item.id === service.id
          ? { ...item, show_in_online_booking: nextValue }
          : item
      )
    );
    setOnlineSavingIds((prev) => {
      const next = new Set(prev);
      next.add(service.id);
      return next;
    });
    try {
      const res = await fetch(`${API}/services/${service.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ show_in_online_booking: nextValue }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to update online booking status");
      }
      const updated = await res.json();
      setServices((prev) =>
        prev.map((item) =>
          item.id === service.id ? { ...item, ...updated } : item
        )
      );
    } catch (err) {
      setServices((prev) =>
        prev.map((item) =>
          item.id === service.id
            ? { ...item, show_in_online_booking: previousValue }
            : item
        )
      );
      setOnlineToggleError(
        err.message ||
          "Failed to update online booking status. Please try again."
      );
    } finally {
      setOnlineSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(service.id);
        return next;
      });
    }
  };

  const handleShowPriceToggle = async (service, nextValue) => {
    if (!service) return;
    if (showPriceSavingIds.has(service.id)) return;
    const previousValue = service.show_price_online !== false;
    if (previousValue === nextValue) return;
    setShowPriceToggleError("");
    setServices((prev) =>
      prev.map((item) =>
        item.id === service.id ? { ...item, show_price_online: nextValue } : item
      )
    );
    setShowPriceSavingIds((prev) => {
      const next = new Set(prev);
      next.add(service.id);
      return next;
    });
    try {
      const res = await fetch(`${API}/services/${service.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ show_price_online: nextValue }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to update online price visibility");
      }
      const updated = await res.json();
      setServices((prev) =>
        prev.map((item) =>
          item.id === service.id ? { ...item, ...updated } : item
        )
      );
    } catch (err) {
      setServices((prev) =>
        prev.map((item) =>
          item.id === service.id
            ? { ...item, show_price_online: previousValue }
            : item
        )
      );
      setShowPriceToggleError(
        err.message ||
          "Failed to update online price visibility. Please try again."
      );
    } finally {
      setShowPriceSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(service.id);
        return next;
      });
    }
  };

  const handleVisibleFlagToggle = async (field, nextValue) => {
    if (!displayServices.length || bulkFlagSaving) return;
    if (field === "show_in_online_booking" && !canManageOnlineBooking) return;

    const itemIds = displayServices.map((service) => service.id).filter(Boolean);
    if (!itemIds.length) return;

    const previousById = new Map(
      displayServices.map((service) => [service.id, service[field]])
    );
    const savingKey = `${field}:${nextValue ? "on" : "off"}`;
    setBulkFlagSaving(savingKey);
    setActiveToggleError("");
    setOnlineToggleError("");
    setShowPriceToggleError("");
    setServices((prev) =>
      prev.map((item) =>
        itemIds.includes(item.id) ? { ...item, [field]: nextValue } : item
      )
    );

    try {
      const res = await fetch(`${API}/services-products/bulk-update-flags`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          salon_id: salonId ? Number(salonId) : undefined,
          item_ids: itemIds,
          [field]: nextValue,
        }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to update visible services.");
      }
      await refreshServices();
    } catch (err) {
      setServices((prev) =>
        prev.map((item) =>
          previousById.has(item.id)
            ? { ...item, [field]: previousById.get(item.id) }
            : item
        )
      );
      const message = err.message || "Failed to update visible services.";
      if (field === "is_active") setActiveToggleError(message);
      if (field === "show_in_online_booking") setOnlineToggleError(message);
      if (field === "show_price_online") setShowPriceToggleError(message);
    } finally {
      setBulkFlagSaving("");
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectedType(null);
    setBulkCategoryId(null);
    setBulkNotice("");
    setBulkCreatePending(false);
  };

  const handleRowSelect = (service, checked) => {
    const rowType = typeForService(service);
    if (checked) {
      if (selectedType && selectedType !== rowType) {
        setBulkNotice("Bulk categorize one type at a time.");
        return;
      }
      const next = new Set(selectedIds);
      next.add(service.id);
      setSelectedIds(next);
      setSelectedType(rowType);
      setBulkNotice("");
      return;
    }
    const next = new Set(selectedIds);
    next.delete(service.id);
    setSelectedIds(next);
    if (next.size === 0) {
      setSelectedType(null);
      setBulkCategoryId(null);
    }
  };

  const handleSelectAll = (checked) => {
    if (!checked) {
      clearSelection();
      return;
    }
    if (!selectableType) {
      setBulkNotice("Select a service or product first to lock bulk selection.");
      return;
    }
    const nextIds = new Set(
      visibleSelectableItems.map((service) => service.id)
    );
    setSelectedIds(nextIds);
    setSelectedType(selectableType);
    setBulkNotice("");
  };

  const handleBulkSave = async () => {
    if (!selectedIds.size || !selectedType) return;
    setBulkSaving(true);
    setBulkError("");
    setBulkSuccess("");
    try {
      const payload = {
        kind: selectedType,
        category_id: bulkCategoryId ?? null,
        item_ids: Array.from(selectedIds),
        mode: "replace",
      };
      const res = await fetch(`${API}/services-products/bulk-assign-category`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to assign category");
      }
      const data = await res.json();
      await refreshServices();
      clearSelection();
      const updatedCount = data?.updated_count ?? 0;
      const skippedIds = data?.skipped_ids || [];
      if (skippedIds.length) {
        setBulkSuccess(
          `Updated ${updatedCount}. Skipped ${skippedIds.length} items.`
        );
      } else {
        setBulkSuccess(`Updated ${updatedCount}.`);
      }
    } catch (err) {
      setBulkError(err.message || "Failed to assign category");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkCategoryChange = (value) => {
    if (value === "__create__") {
      setBulkCreatePending(true);
      setEditingCategoryId(null);
      setCategoryForm({
        name: "",
        kind: selectedType === "product" ? "product" : "service",
        sort_order: 0,
        is_active: true,
      });
      setCategoryError("");
      setTimeout(() => {
        if (categoryFormRef.current) {
          categoryFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
      return;
    }
    setBulkCreatePending(false);
    if (!value) {
      setBulkCategoryId(null);
    } else {
      setBulkCategoryId(Number(value));
    }
  };

  const resetCategoryForm = () => {
    setEditingCategoryId(null);
    setCategoryForm({
      name: "",
      kind: "both",
      sort_order: 0,
      is_active: true,
    });
  };

  const handleCategoryEdit = (category) => {
    setEditingCategoryId(category.id);
    setCategoryForm({
      name: category.name || "",
      kind: category.kind || "both",
      sort_order: category.sort_order ?? 0,
      is_active: category.is_active ?? true,
    });
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    const nameValue = categoryForm.name.trim();
    if (!nameValue) {
      setCategoryError("Category name is required.");
      return;
    }
    setSaving(true);
    setCategoryError("");
    try {
      const payload = {
        name: nameValue,
        kind: categoryForm.kind,
        sort_order: Number(categoryForm.sort_order) || 0,
        is_active: !!categoryForm.is_active,
      };
      const url = editingCategoryId
        ? `${API}/categories/${editingCategoryId}`
        : `${API}/categories`;
      const method = editingCategoryId ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to save category");
      }
      const saved = await res.json();
      resetCategoryForm();
      await refreshCategories();
      if (bulkCreatePending && saved?.id) {
        setBulkCategoryId(saved.id);
        setBulkCreatePending(false);
      }
    } catch (err) {
      setCategoryError(err.message || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryDelete = async (categoryId) => {
    setSaving(true);
    setCategoryError("");
    try {
      const res = await fetch(`${API}/categories/${categoryId}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to delete category");
      }
      await refreshCategories();
      await refreshServices();
      if (editingCategoryId === categoryId) {
        resetCategoryForm();
      }
    } catch (err) {
      setCategoryError(err.message || "Failed to delete category");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };


  return (
    <>
      <div className="manageSectionShell min-h-screen bg-slate-100">
        <div className="manageSectionWide manage-section-wide manage-section-compact">
          <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="bg-rose-500 px-6 py-4 relative">
              <h1 className="text-xl md:text-2xl font-semibold text-white">
                Manage Services & Products
              </h1>
              <p className="text-sm md:text-base text-rose-100">
                Manage services and products offered by your salon.
              </p>
              <div
                className="absolute right-4 top-3 z-50"
                onClick={handleClose}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <ModalCloseX onClick={handleClose} />
              </div>
            </div>
            <div className={`px-6 py-6 space-y-6 ${modalText}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/manager/manage/service-imports")}
                    className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
                  >
                    Import History
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsImportOpen(true)}
                    className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
                  >
                    Import Services
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsProductImportOpen(true)}
                    className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
                  >
                    Import Products
                  </button>
                </div>
              </div>


            {error && (

              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">

                {error}

              </div>

            )}



            {loading ? (

              <div className="text-slate-500">Loading...</div>

            ) : (

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                <div className={`lg:col-span-8 ${sectionCard}`}>

                  <div className="flex items-center justify-between">
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      Services & Products
                    </h2>
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs md:text-sm font-semibold text-slate-600">
                      {displayServices.length} total
                    </span>
                  </div>
                  <div className="mt-4">
                    <div className="max-w-md">
                      <label className={labelText}>Search services &amp; products...</label>
                      <div className="mt-2">
                        <ServiceSearch
                          services={services}
                          value={serviceQuery}
                          onChange={setServiceQuery}
                          onPick={(service) =>
                            setServiceQuery(service?.name || "")
                          }
                          placeholder="Search services & products..."
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <label className="inline-flex items-center gap-3 text-sm md:text-base text-slate-700">
                      <input
                        type="checkbox"
                        checked={groupByCategory}
                        onChange={(e) => setGroupByCategory(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="font-semibold text-slate-700">
                        Group by category
                      </span>
                    </label>
                  </div>
                  {(bulkError ||
                    bulkSuccess ||
                    bulkNotice ||
                    activeToggleError ||
                    onlineToggleError ||
                    showPriceToggleError) && (
                    <div className="mt-4 space-y-2">
                      {bulkError && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                          {bulkError}
                        </div>
                      )}
                      {activeToggleError && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                          {activeToggleError}
                        </div>
                      )}
                      {onlineToggleError && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                          {onlineToggleError}
                        </div>
                      )}
                      {showPriceToggleError && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                          {showPriceToggleError}
                        </div>
                      )}
                      {bulkSuccess && (
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 text-sm md:text-base">
                          {bulkSuccess}
                        </div>
                      )}
                      {bulkNotice && (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-600 text-sm md:text-base">
                          {bulkNotice}
                        </div>
                      )}
                    </div>
                  )}
                  {selectedIds.size > 0 && (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 flex flex-wrap items-center gap-3">
                      <span className="text-sm md:text-base font-semibold text-slate-700">
                        {selectedIds.size} selected
                      </span>
                      <select
                        value={
                          bulkCreatePending ? "__create__" : bulkCategoryId ?? ""
                        }
                        onChange={(e) => handleBulkCategoryChange(e.target.value)}
                        className={`min-w-[220px] ${fieldClass}`}
                      >
                        <option value="">Uncategorized</option>
                        {bulkCategoryOptions.map((category) => {
                          const isInactive = !category.is_active;
                          const isSelected = bulkCategoryId === category.id;
                          return (
                            <option
                              key={category.id}
                              value={category.id}
                              disabled={isInactive && !isSelected}
                            >
                              {category.name}
                              {isInactive ? " (inactive)" : ""}
                            </option>
                          );
                        })}
                        <option value="__create__">+ Create new category...</option>
                      </select>
                      <button
                        type="button"
                        disabled={bulkSaving}
                        onClick={handleBulkSave}
                        className={`${btnPrimary}`}
                      >
                        {bulkSaving ? "Saving..." : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={clearSelection}
                        className={btnSecondary}
                      >
                        Clear selection
                      </button>
                    </div>
                  )}
                  <div className="mt-4 w-full overflow-x-auto overscroll-x-contain">
                    <table className="min-w-[1040px] w-full table-fixed">
                      <thead>
                        <tr>
                          <th
                            className={`${tableHeaderClass} w-10 text-center`}
                          >
                            <input
                              ref={selectAllRef}
                              type="checkbox"
                              checked={allVisibleSelected}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              disabled={!sortedDisplayServices.length}
                              className="h-4 w-4 rounded border-slate-300 text-slate-900"
                            />
                          </th>
                          <SortableTh
                            label="Name"
                            sortKey="name"
                            sortState={servicesSort.sortState}
                            onSort={servicesSort.handleSort}
                            className={tableHeaderClass}
                            align="left"
                            buttonClassName={tableHeaderButtonClass}
                          />
                          <SortableTh
                            label="Duration"
                            sortKey="duration_minutes"
                            sortState={servicesSort.sortState}
                            onSort={servicesSort.handleSort}
                            className={`${tableHeaderClass} w-24`}
                            align="center"
                            buttonClassName={tableHeaderButtonClass}
                          />
                          <SortableTh
                            label="Price"
                            sortKey="base_price"
                            sortState={servicesSort.sortState}
                            onSort={servicesSort.handleSort}
                            className={`${tableHeaderClass} w-24`}
                            align="center"
                            buttonClassName={tableHeaderButtonClass}
                          />
                          <th className={`${tableHeaderClass} col-active text-center`}>
                            <div className="flex flex-col items-center gap-1">
                              <input
                                ref={activeAllRef}
                                type="checkbox"
                                checked={activeVisibleState.all}
                                disabled={!displayServices.length || !!bulkFlagSaving}
                                onChange={() =>
                                  handleVisibleFlagToggle(
                                    "is_active",
                                    activeVisibleState.nextValue
                                  )
                                }
                                title="Set active for all visible items"
                                className="h-4 w-4 rounded border-slate-300 text-slate-900"
                              />
                              <button
                                type="button"
                                onClick={() => servicesSort.handleSort("is_active")}
                                className={`font-semibold ${tableHeaderButtonClass}`}
                              >
                                Active
                              </button>
                            </div>
                          </th>
                          <th className={`${tableHeaderClass} w-32 text-center`}>
                            <div className="flex flex-col items-center gap-1">
                              <input
                                ref={onlineAllRef}
                                type="checkbox"
                                checked={onlineVisibleState.all}
                                disabled={
                                  !displayServices.length ||
                                  !!bulkFlagSaving ||
                                  !canManageOnlineBooking
                                }
                                onChange={() =>
                                  handleVisibleFlagToggle(
                                    "show_in_online_booking",
                                    onlineVisibleState.nextValue
                                  )
                                }
                                title="Set online booking for all visible items"
                                className="h-4 w-4 rounded border-slate-300 text-slate-900"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  servicesSort.handleSort("show_in_online_booking")
                                }
                                className={`font-semibold ${tableHeaderButtonClass}`}
                              >
                                Online Booking
                              </button>
                            </div>
                          </th>
                          <th className={`${tableHeaderClass} w-32 text-center`}>
                            <div className="flex flex-col items-center gap-1">
                              <input
                                ref={showPriceAllRef}
                                type="checkbox"
                                checked={showPriceVisibleState.all}
                                disabled={!displayServices.length || !!bulkFlagSaving}
                                onChange={() =>
                                  handleVisibleFlagToggle(
                                    "show_price_online",
                                    showPriceVisibleState.nextValue
                                  )
                                }
                                title="Set price visibility for all visible items"
                                className="h-4 w-4 rounded border-slate-300 text-slate-900"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  servicesSort.handleSort("show_price_online")
                                }
                                className={`font-semibold ${tableHeaderButtonClass}`}
                              >
                                Show Price Online
                              </button>
                            </div>
                          </th>
                          <th
                            className={`${tableHeaderClass} text-center w-40`}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="border-t border-slate-100">
                        {groupByCategory && groupedServices
                          ? groupedServices.map((group, groupIndex) => {
                            const label = group.category
                              ? `${group.category.name}${group.category.is_active ? "" : " (inactive)"}`
                              : "Uncategorized";
                            return (
                              <Fragment
                                key={`${group.category?.id || "uncategorized"}-${groupIndex}`}
                              >
                                <tr className="bg-slate-50 border-b border-slate-100">
                                  <td
                                    colSpan={8}
                                    className="px-4 py-3 text-slate-700 text-sm md:text-base font-semibold"
                                  >
                                    <div className="flex items-center justify-between">
                                      <span>{label}</span>
                                      <span className="text-xs md:text-sm text-slate-500">
                                        {group.items.length} items
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                                {group.items.map((svc) => (
                                  <tr
                                    key={svc.id}
                                    className={`border-b border-slate-100 ${
                                      svc.is_active ? "" : "opacity-70"
                                    }`}
                                  >
                                    <td className="px-4 py-3 w-10 text-center">
                                      <input
                                        type="checkbox"
                                        checked={selectedIds.has(svc.id)}
                                        disabled={
                                          selectedType &&
                                          selectedType !== typeForService(svc)
                                        }
                                        title={
                                          selectedType &&
                                          selectedType !== typeForService(svc)
                                            ? "Bulk categorize one type at a time."
                                            : ""
                                        }
                                        onChange={(e) =>
                                          handleRowSelect(svc, e.target.checked)
                                        }
                                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                                      />
                                    </td>
                                    <td className="px-4 py-3 font-semibold text-slate-900 text-sm md:text-base">
                                      <span className="block truncate" title={svc.name}>
                                        {svc.name}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-700 text-sm md:text-base text-center">
                                      {svc.is_product
                                        ? "N/A"
                                        : svc.duration_minutes
                                          ? `${svc.duration_minutes} min`
                                          : "N/A"}
                                    </td>
                                    <td className="px-4 py-3 text-slate-700 text-sm md:text-base text-center">
                                      {formatPrice(svc.base_price)}
                                    </td>
                                    <td className="px-4 py-3 text-sm md:text-base col-active">
                                      <div className="whitespace-nowrap flex items-center justify-center">
                                        <input
                                          type="checkbox"
                                          checked={!!svc.is_active}
                                          disabled={activeSavingIds.has(svc.id)}
                                          onChange={(e) =>
                                            handleActiveToggle(svc, e.target.checked)
                                          }
                                          className="h-4 w-4 rounded border-slate-300 text-slate-900"
                                        />
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm md:text-base text-center">
                                      <div className="whitespace-nowrap flex items-center justify-center">
                                        <input
                                          type="checkbox"
                                          checked={!!svc.show_in_online_booking}
                                          disabled={
                                            !canManageOnlineBooking ||
                                            onlineSavingIds.has(svc.id)
                                          }
                                          onChange={(e) =>
                                            handleOnlineToggle(
                                              svc,
                                              e.target.checked
                                            )
                                          }
                                          className="h-4 w-4 rounded border-slate-300 text-slate-900"
                                        />
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm md:text-base text-center">
                                      <div className="whitespace-nowrap flex items-center justify-center">
                                        <input
                                          type="checkbox"
                                          checked={svc.show_price_online !== false}
                                          disabled={showPriceSavingIds.has(svc.id)}
                                          onChange={(e) =>
                                            handleShowPriceToggle(
                                              svc,
                                              e.target.checked
                                            )
                                          }
                                          className="h-4 w-4 rounded border-slate-300 text-slate-900"
                                        />
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 w-40 text-center">
                                      <div className="whitespace-nowrap flex items-center justify-center gap-3">
                                        <button
                                          onClick={() => handleEdit(svc)}
                                          className="h-9 px-4 rounded-full border border-slate-300 text-slate-700 text-sm md:text-base font-semibold hover:border-slate-400 hover:bg-slate-50"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          onClick={() => handleDelete(svc.id)}
                                          className="h-9 px-4 rounded-full text-rose-600 text-sm md:text-base font-semibold hover:bg-rose-50"
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </Fragment>
                            );
                          })
                          : sortedDisplayServices.map((svc) => (
                            <tr
                              key={svc.id}
                              className={`border-b border-slate-100 ${
                                svc.is_active ? "" : "opacity-70"
                              }`}
                            >
                              <td className="px-4 py-3 w-10 text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(svc.id)}
                                  disabled={
                                    selectedType &&
                                    selectedType !== typeForService(svc)
                                  }
                                  title={
                                    selectedType &&
                                    selectedType !== typeForService(svc)
                                      ? "Bulk categorize one type at a time."
                                      : ""
                                  }
                                  onChange={(e) =>
                                    handleRowSelect(svc, e.target.checked)
                                  }
                                  className="h-4 w-4 rounded border-slate-300 text-slate-900"
                                />
                              </td>
                              <td className="px-4 py-3 font-semibold text-slate-900 text-sm md:text-base">
                                <span className="block truncate" title={svc.name}>
                                  {svc.name}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-700 text-sm md:text-base text-center">
                                {svc.is_product
                                  ? "N/A"
                                  : svc.duration_minutes
                                    ? `${svc.duration_minutes} min`
                                    : "N/A"}
                              </td>
                              <td className="px-4 py-3 text-slate-700 text-sm md:text-base text-center">
                                {formatPrice(svc.base_price)}
                              </td>
                              <td className="px-4 py-3 text-sm md:text-base col-active">
                                <div className="whitespace-nowrap flex items-center justify-center">
                                  <input
                                    type="checkbox"
                                    checked={!!svc.is_active}
                                    disabled={activeSavingIds.has(svc.id)}
                                    onChange={(e) =>
                                      handleActiveToggle(svc, e.target.checked)
                                    }
                                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                                  />
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm md:text-base text-center">
                                <div className="whitespace-nowrap flex items-center justify-center">
                                  <input
                                    type="checkbox"
                                    checked={!!svc.show_in_online_booking}
                                    disabled={
                                      !canManageOnlineBooking ||
                                      onlineSavingIds.has(svc.id)
                                    }
                                    onChange={(e) =>
                                      handleOnlineToggle(svc, e.target.checked)
                                    }
                                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                                  />
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm md:text-base text-center">
                                <div className="whitespace-nowrap flex items-center justify-center">
                                  <input
                                    type="checkbox"
                                    checked={svc.show_price_online !== false}
                                    disabled={showPriceSavingIds.has(svc.id)}
                                    onChange={(e) =>
                                      handleShowPriceToggle(svc, e.target.checked)
                                    }
                                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                                  />
                                </div>
                              </td>
                              <td className="px-4 py-3 w-40 text-center">
                                <div className="whitespace-nowrap flex items-center justify-center gap-3">
                                  <button
                                    onClick={() => handleEdit(svc)}
                                    className="h-9 px-4 rounded-full border border-slate-300 text-slate-700 text-sm md:text-base font-semibold hover:border-slate-400 hover:bg-slate-50"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDelete(svc.id)}
                                    className="h-9 px-4 rounded-full text-rose-600 text-sm md:text-base font-semibold hover:bg-rose-50"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        {!sortedDisplayServices.length && (
                          <tr>
                            <td
                              className="px-4 py-6 text-slate-500 text-center"
                              colSpan={8}
                            >
                              {serviceQuery.trim()
                                ? "No services or products match this search."
                                : "No services or products yet."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                </div>



                <div className="lg:col-span-4 space-y-6">
                  <div className={sectionCard}>
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Categories
                      </h2>
                      {categoriesLoading && (
                        <span className="text-xs md:text-sm text-slate-500">
                          Loading...
                        </span>
                      )}
                    </div>
                    {categoryError && (
                      <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                        {categoryError}
                      </div>
                    )}
                    <div className="mt-4">
                      <label className={labelText}>Category Filter</label>
                      <select
                        value={categoryFilterKind}
                        onChange={(e) => setCategoryFilterKind(e.target.value)}
                        className={`mt-2 w-full ${fieldClass}`}
                      >
                        <option value="all">All</option>
                        <option value="service">Service</option>
                        <option value="product">Product</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div className="mt-4 space-y-3">
                      {filteredCategories.map((category) => (
                        <div
                          key={category.id}
                          className="rounded-2xl border border-slate-200 px-4 py-3 flex items-center justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 truncate">
                              {category.name}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs md:text-sm text-slate-500">
                              <span className="inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5">
                                {category.kind === "service"
                                  ? "Service"
                                  : category.kind === "product"
                                    ? "Product"
                                    : "Both"}
                              </span>
                              {!category.is_active && (
                                <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5">
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleCategoryEdit(category)}
                              className="h-9 px-4 rounded-full border border-slate-300 text-slate-700 text-sm md:text-base font-semibold hover:border-slate-400 hover:bg-slate-50"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCategoryDelete(category.id)}
                              className="h-9 px-4 rounded-full text-rose-600 text-sm md:text-base font-semibold hover:bg-rose-50"
                            >
                              {category.is_active ? "Disable" : "Delete"}
                            </button>
                          </div>
                        </div>
                      ))}
                      {!filteredCategories.length && (
                        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-center text-slate-500 text-sm md:text-base">
                          No categories yet.
                        </div>
                      )}
                    </div>
                    <form
                      ref={categoryFormRef}
                      className="mt-6 space-y-4"
                      onSubmit={handleCategorySubmit}
                    >
                      <div className="flex flex-col gap-1">
                        <label className={`${labelText} uppercase`}>
                          Category Name
                        </label>
                        <input
                          type="text"
                          value={categoryForm.name}
                          onChange={(e) =>
                            setCategoryForm((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          className={`w-full ${fieldClass}`}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className={`${labelText} uppercase`}>
                          Category Kind
                        </label>
                        <select
                          value={categoryForm.kind}
                          onChange={(e) =>
                            setCategoryForm((prev) => ({
                              ...prev,
                              kind: e.target.value,
                            }))
                          }
                          className={`w-full ${fieldClass}`}
                        >
                          <option value="service">Service</option>
                          <option value="product">Product</option>
                          <option value="both">Both</option>
                        </select>
                      </div>
                      <label className="inline-flex items-center gap-3 text-sm md:text-base text-slate-700">
                        <input
                          type="checkbox"
                          checked={!!categoryForm.is_active}
                          onChange={(e) =>
                            setCategoryForm((prev) => ({
                              ...prev,
                              is_active: e.target.checked,
                            }))
                          }
                          className="h-4 w-4 rounded border-slate-300 text-slate-900"
                        />
                        <span className="font-semibold text-slate-700">Active</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          type="submit"
                          disabled={saving}
                          className={`flex-1 ${btnPrimary}`}
                        >
                          {saving
                            ? "Saving..."
                            : editingCategoryId
                              ? "Save Category"
                              : "Add Category"}
                        </button>
                        {editingCategoryId && (
                          <button
                            type="button"
                            onClick={resetCategoryForm}
                            className={btnSecondary}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </form>
                  </div>

                  <div className={sectionCard}>
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      {editingId
                        ? isProduct
                          ? "Edit Product"
                          : "Edit Service"
                        : "Add Service / Product"}
                    </h2>
                    <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
                      <div className="flex flex-col gap-1">
                        <label className={`${labelText} uppercase`}>
                          Name
                        </label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, name: e.target.value }))
                          }
                          required
                          className={`w-full ${fieldClass}`}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className={`${labelText} uppercase`}>Type</label>
                        <select
                          value={isProduct ? "product" : "service"}
                          onChange={(e) =>
                            handleTypeChange(e.target.value === "product")
                          }
                          className={`w-full ${fieldClass}`}
                        >
                          <option value="service">Service</option>
                          <option value="product">Product</option>
                        </select>
                      </div>
                      {!editingId && (
                        <div className="flex flex-col gap-1">
                          <label className="inline-flex items-center gap-3 text-sm md:text-base text-slate-700">
                            <input
                              type="checkbox"
                              checked={assignToAllStaff}
                              onChange={(e) => setAssignToAllStaff(e.target.checked)}
                              className="h-4 w-4 rounded border-slate-300 text-slate-900"
                            />
                            <span className="font-semibold text-slate-700">
                              Assign to all staff
                            </span>
                          </label>
                          <p className="text-xs md:text-sm text-slate-500">
                            Automatically make this available for every staff member in this salon.
                          </p>
                        </div>
                      )}
                      <div className="flex flex-col gap-1">
                        <label className={`${labelText} uppercase`}>
                          Category (optional)
                        </label>
                        <select
                          value={form.category_id ?? ""}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              category_id: e.target.value ? Number(e.target.value) : null,
                            }))
                          }
                          className={`w-full ${fieldClass}`}
                        >
                          <option value="">Uncategorized</option>
                          {categoryOptions.map((category) => {
                            const isInactive = !category.is_active;
                            const isSelected = form.category_id === category.id;
                            const label = `${category.name}${category.incompatible ? " (incompatible)" : ""}${
                              isInactive ? " (inactive)" : ""
                            }`;
                            return (
                              <option
                                key={category.id}
                                value={category.id}
                                disabled={isInactive && !isSelected}
                              >
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className={`${labelText} uppercase`}>SKU</label>
                        <input
                          type="text"
                          value={form.sku}
                          onChange={(e) => {
                            setForm((f) => ({ ...f, sku: e.target.value }));
                            if (skuError) setSkuError("");
                          }}
                          disabled={!isProduct}
                          required={isProduct}
                          className={`w-full ${fieldClass}${
                            isProduct ? "" : " bg-slate-100 text-slate-500"
                          }`}
                        />
                        {isProduct ? (
                          skuError && (
                            <p className="text-xs md:text-sm text-rose-600">
                              {skuError}
                            </p>
                          )
                        ) : (
                          <p className="text-xs md:text-sm text-slate-500">
                            SKU is only required for products.
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1">
                          <label className={`${labelText} uppercase`}>
                            Duration (minutes)
                          </label>
                          {isProduct ? (
                            <input
                              type="text"
                              value="N/A"
                              disabled
                              className={`w-full ${fieldClass} bg-slate-100 text-slate-500`}
                            />
                          ) : (
                            <select
                              value={snappedDuration}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  duration_minutes: Number(e.target.value),
                                }))
                              }
                              className={`w-full ${fieldClass}`}
                            >
                              {DURATION_OPTIONS.map((minutes) => (
                                <option key={minutes} value={minutes}>
                                  {formatDurationOption(minutes)}
                                </option>
                              ))}
                            </select>
                          )}
                          {showDurationHint && (
                            <p className="text-xs md:text-sm text-slate-500">
                              This service was previously set to {rawDuration} minutes.
                              Select a 15-minute interval to update.
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className={`${labelText} uppercase`}>Price</label>
                          <input
                            type="number"
                            step="0.01"
                            value={form.base_price}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, base_price: e.target.value }))
                            }
                            className={`w-full ${fieldClass}`}
                          />
                        </div>
                      </div>
                      <label className="inline-flex items-start gap-3 text-sm md:text-base text-slate-700">
                        <input
                          type="checkbox"
                          checked={form.show_price_online !== false}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              show_price_online: e.target.checked,
                            }))
                          }
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900"
                        />
                        <span>
                          <span className="block font-semibold text-slate-700">
                            Show price online
                          </span>
                          <span className="block text-xs md:text-sm text-slate-500">
                            Turn this off when clients should see Price varies instead
                            of the exact amount.
                          </span>
                        </span>
                      </label>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                        <button
                          type="button"
                          onClick={() =>
                            setShowAdvancedAiPricing((current) => !current)
                          }
                          className="text-sm md:text-base font-semibold text-slate-700 hover:text-slate-900"
                        >
                          {showAdvancedAiPricing ? "Hide" : "Show"} advanced AI
                          pricing options
                        </button>
                        {showAdvancedAiPricing && (
                          <div className="mt-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="flex flex-col gap-1">
                                <label className={`${labelText} uppercase`}>
                                  AI pricing behavior
                                </label>
                                <select
                                  value={form.ai_pricing_response_mode}
                                  onChange={(e) =>
                                    setForm((f) => ({
                                      ...f,
                                      ai_pricing_response_mode: e.target.value,
                                    }))
                                  }
                                  className={`w-full ${fieldClass}`}
                                >
                                  {AI_PRICING_RESPONSE_OPTIONS.map((option) => (
                                    <option
                                      key={option.value || "default"}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                <p className="text-xs md:text-sm text-slate-500">
                                  Use this only when this service needs different AI
                                  wording from the Client Booking Helper settings.
                                </p>
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className={`${labelText} uppercase`}>
                                  Starting at price (optional)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={form.ai_minimum_price}
                                  onChange={(e) =>
                                    setForm((f) => ({
                                      ...f,
                                      ai_minimum_price: e.target.value,
                                    }))
                                  }
                                  className={`w-full ${fieldClass}`}
                                  placeholder="Use when AI pricing is variable"
                                />
                                <p className="text-xs md:text-sm text-slate-500">
                                  If blank, the helper falls back to the service
                                  price for starting-at answers.
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="flex flex-col gap-1">
                                <label className={`${labelText} uppercase`}>
                                  AI pricing disclaimer (optional)
                                </label>
                                <textarea
                                  value={form.ai_pricing_disclaimer}
                                  onChange={(e) =>
                                    setForm((f) => ({
                                      ...f,
                                      ai_pricing_disclaimer: e.target.value,
                                    }))
                                  }
                                  className="min-h-[96px] w-full rounded-xl border border-slate-300 px-4 py-3 text-base md:text-lg focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                  rows={3}
                                  placeholder="Example: Final pricing depends on length, design complexity, and condition."
                                />
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className={`${labelText} uppercase`}>
                                  Hidden-price assistant message (optional)
                                </label>
                                <textarea
                                  value={form.ai_hidden_price_message}
                                  onChange={(e) =>
                                    setForm((f) => ({
                                      ...f,
                                      ai_hidden_price_message: e.target.value,
                                    }))
                                  }
                                  className="min-h-[96px] w-full rounded-xl border border-slate-300 px-4 py-3 text-base md:text-lg focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                  rows={3}
                                  placeholder="Example: Pricing depends on the details, and we can give you an exact quote at booking."
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className={`${labelText} uppercase`}>
                          Description (optional)
                        </label>
                        <textarea
                          value={form.description}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, description: e.target.value }))
                          }
                          className="min-h-[120px] w-full rounded-xl border border-slate-300 px-4 py-3 text-base md:text-lg focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
                          rows={3}
                          placeholder="Short description"
                        />
                      </div>
                      <label className="inline-flex items-center gap-3 text-sm md:text-base text-slate-700">
                        <input
                          type="checkbox"
                          checked={!!form.is_active}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, is_active: e.target.checked }))
                          }
                          className="h-4 w-4 rounded border-slate-300 text-slate-900"
                        />
                        <span className="font-semibold text-slate-700">Active</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          type="submit"
                          disabled={saving}
                          className={`flex-1 ${btnPrimary}`}
                        >
                          {saving
                            ? "Saving..."
                            : editingId
                              ? "Save Changes"
                              : isProduct
                                ? "Add Product"
                                : "Add Service"}
                        </button>
                        {editingId && (
                          <button
                            type="button"
                            onClick={resetForm}
                            className={btnSecondary}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>

            )}

          </div>
        </div>
      </div>
    </div>
      <SBINModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        title="Import Services"
        size="xxl"
      >
        <ServiceImportWizard
          onComplete={() => {
            refreshServices();
          }}
          onClose={() => setIsImportOpen(false)}
          initialSalonId={salonId}
        />
      </SBINModal>
      <ImportProductsModal
        isOpen={isProductImportOpen}
        onClose={() => setIsProductImportOpen(false)}
        initialSalonId={salonId}
        onComplete={() => {
          refreshServices();
          setIsProductImportOpen(false);
        }}
      />
    </>
  );


}

